import { Body, Button, Flex, Image, transition } from '@hover/blueprint';
import type { FlexProps, SystemStyleObject } from '@hover/blueprint/chakra';
import { resetFocus } from '@hover/blueprint/foundation';
import React from 'react';

export type CircleSelectionProps = {
  isSelected?: boolean;
  height?: string;
  width?: string;
  hover?: boolean;
  bgColor?: string;
  isThin?: boolean;
  sx?: object;
};

export const selectionStyles = ({
  isSelected,
  bgColor,
  isThin = false,
  sx,
}: CircleSelectionProps) => {
  const shadow = isThin ? 'ringPrimary' : 'haloPrimary';
  return {
    background: isSelected ? 'transparent' : 'neutral.200',
    overflow: 'visible',
    borderWidth: isSelected ? '0px' : '3px',
    borderStyle: bgColor ? 'solid' : 'none',
    borderColor: isSelected ? 'transparent' : 'neutral.300',
    borderRadius: '500',
    _hover: {
      backgroundColor: bgColor
        ? `${bgColor} !important`
        : 'transparent !important',
      border: '0px solid transparent',
    },
    sx: { ...sx, ...resetFocus({ shadow }) } as SystemStyleObject,
  };
};

export const parentSelectionStyles = ({
  isThin = false,
  sx,
}: CircleSelectionProps) => {
  const shadow = isThin ? 'ringPrimary' : 'haloPrimary';

  return {
    width: 'fit-content',
    marginBottom: '6px',
    ...transition.easing250('box-shadow'),
    sx: { ...sx, ...resetFocus({ shadow }) } as SystemStyleObject,
  };
};

export interface CircleButtonProps extends FlexProps {
  isSelected: boolean;
  dataTestId: string;
  label: string;
  onClick: () => void;
  text?: string;
  imgSrc?: string;
  color?: string;
  outlineOffset: string;
  buttonSize: string;
}

export const CircleButton = ({
  isSelected,
  text,
  imgSrc,
  color,
  onClick,
  dataTestId,
  label,
  outlineOffset,
  buttonSize,
  ...other
}: CircleButtonProps) => (
  <Flex
    alignContent="center"
    alignItems="center"
    border="none"
    direction="column"
    padding="6px"
    {...other}
    data-testid={`CircleButtonContainer:${dataTestId}`}
  >
    <Flex {...parentSelectionStyles({})}>
      <Flex
        _hover={{
          outlineColor: isSelected ? undefined : 'darkAlpha.500',
        }}
        alignContent="center"
        border="none"
        borderRadius={900}
        direction="column"
        outlineColor={isSelected ? 'neutral.900' : 'transparent'}
        outlineOffset={outlineOffset}
        sx={{
          outlineStyle: 'solid',
          outlineWidth: '3px',
        }}
        {...transition.easing250('outline-color')}
      >
        <Button
          {...selectionStyles({ isSelected, bgColor: color })}
          bg={color ?? '#ffffff'}
          borderRadius={900}
          data-testid={dataTestId}
          height={buttonSize}
          label={label}
          onClick={onClick}
          shape="circle"
          width={buttonSize}
        >
          {imgSrc && (
            <Image
              alt="alt"
              bg={color ?? '#ffffff'}
              borderRadius="900"
              height="100%"
              src={imgSrc ?? ''}
              width="100%"
            />
          )}
        </Button>
      </Flex>
    </Flex>

    {text && (
      <Body alignSelf="center" my={0}>
        {text}
      </Body>
    )}
  </Flex>
);
